import { bioThemes } from './bioThemes'
import type { BioLink } from './BioLinkEditor'
import type { BioProduct } from './BioProductEditor'

interface BioPageData {
  title: string
  bio: string | null
  profile_image_url: string | null
  display_name?: string | null
  handle?: string | null
  theme: string
  background_type: string
  background_value: string | null
  button_style: string
  button_color: string
  text_color: string
  font_family: string
}

interface Props {
  page: BioPageData
  links: BioLink[]
  products?: BioProduct[]
}

function getButtonRadius(style: string) {
  switch (style) {
    case 'square': return '8px'
    case 'pill': return '50px'
    default: return '12px'
  }
}

function getBackground(page: BioPageData) {
  const theme = bioThemes[page.theme] || bioThemes.default
  if (page.background_type === 'gradient' && page.background_value) return page.background_value
  if (page.background_type === 'image' && page.background_value) return `url(${page.background_value}) center/cover no-repeat`
  if (page.background_type === 'color' && page.background_value) return page.background_value
  return theme.background
}

function formatPrice(price: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price)
  } catch {
    return `${currency} ${price.toFixed(2)}`
  }
}

function getBadgeLabel(badge: string, badgeText: string | null) {
  if (badge === 'custom' && badgeText) return badgeText
  const labels: Record<string, string> = {
    top_pick: 'Top Pick',
    sale: 'Sale',
    new: 'New',
    limited: 'Limited',
  }
  return labels[badge] || badge
}

export function BioPhonePreview({ page, links, products = [] }: Props) {
  const theme = bioThemes[page.theme] || bioThemes.default
  const buttonColor = page.button_color || theme.buttonBg
  const textColor = page.text_color || theme.textColor
  const buttonRadius = getButtonRadius(page.button_style)
  const background = getBackground(page)
  const isNeon = page.theme === 'neon'
  const buttonBg = isNeon ? 'transparent' : buttonColor
  const buttonTextColor = isNeon ? buttonColor : (theme.buttonText || '#ffffff')
  const buttonBorder = isNeon ? `2px solid ${buttonColor}` : 'none'

  const activeLinks = links.filter(l => l.is_active)
  const activeProducts = products.filter(p => p.is_active)

  return (
    <div className="flex justify-center">
      {/* Phone frame */}
      <div className="w-[280px] h-[560px] rounded-[36px] border-[6px] border-gray-800 dark:border-gray-600 bg-gray-800 dark:bg-gray-600 p-1 shadow-2xl">
        {/* Screen */}
        <div
          className="w-full h-full rounded-[30px] overflow-y-auto overflow-x-hidden"
          style={{
            background,
            fontFamily: `'${page.font_family}', sans-serif`,
            color: textColor,
          }}
        >
          {/* Notch */}
          <div className="flex justify-center pt-2 pb-4">
            <div className="w-20 h-5 bg-gray-800 dark:bg-gray-600 rounded-full" />
          </div>

          {/* Content */}
          <div className="px-5 pb-6 flex flex-col items-center gap-3">
            {/* Profile */}
            {page.profile_image_url ? (
              <img
                src={page.profile_image_url}
                alt={page.title}
                className="w-16 h-16 rounded-full object-cover border-2"
                style={{ borderColor: 'rgba(255,255,255,0.3)' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold"
                style={{ background: buttonColor, color: buttonTextColor }}
              >
                {(page.title || 'U').charAt(0).toUpperCase()}
              </div>
            )}

            <h2
              className="text-base font-bold text-center"
              style={{ color: theme.profileTextColor || textColor }}
            >
              {page.display_name || page.title || 'Your Name'}
            </h2>

            {page.handle && (
              <p
                className="text-[10px] text-center -mt-1"
                style={{ color: theme.bioTextColor || textColor, opacity: 0.7 }}
              >
                @{page.handle}
              </p>
            )}

            {page.bio && (
              <p
                className="text-[11px] text-center leading-relaxed max-w-[220px]"
                style={{ color: theme.bioTextColor || textColor }}
              >
                {page.bio}
              </p>
            )}

            {/* Links */}
            <div className="w-full space-y-2.5 mt-2">
              {activeLinks.length === 0 && activeProducts.length === 0 ? (
                <div className="text-center py-4 opacity-40 text-[11px]">
                  Add links to see them here
                </div>
              ) : (
                activeLinks.map(link => (
                  <div
                    key={link.id}
                    className="w-full flex items-center justify-center py-2.5 px-4 text-[12px] font-semibold cursor-default transition-all hover:opacity-80"
                    style={{
                      background: buttonBg,
                      color: buttonTextColor,
                      borderRadius: buttonRadius,
                      border: buttonBorder,
                    }}
                  >
                    {link.icon && <span className="mr-1.5">{link.icon}</span>}
                    {link.title}
                  </div>
                ))
              )}
            </div>

            {/* Products */}
            {activeProducts.length > 0 && (
              <div className="w-full mt-3">
                <p
                  className="text-[11px] font-semibold mb-2"
                  style={{ color: theme.profileTextColor || textColor }}
                >
                  {activeProducts[0]?.section_title || 'Featured Products'}
                </p>
                <div
                  className="w-full"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: activeProducts[0]?.section_layout === 'single' ? '1fr' : 'repeat(2, 1fr)',
                    gap: '8px',
                  }}
                >
                  {activeProducts.map(product => (
                    <div
                      key={product.id}
                      className="rounded-lg overflow-hidden cursor-default"
                      style={{
                        background: isNeon ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.15)',
                        border: isNeon ? `1px solid ${buttonColor}33` : '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {/* Product image */}
                      {product.image_url && (
                        <div className="w-full aspect-square relative">
                          <img
                            src={product.image_url}
                            alt={product.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                          {product.badge && (
                            <span
                              className="absolute top-1 left-1 text-[8px] font-bold px-1.5 py-0.5 rounded"
                              style={{
                                background: product.badge === 'sale' ? '#ef4444' : buttonColor,
                                color: '#fff',
                              }}
                            >
                              {getBadgeLabel(product.badge, product.badge_text)}
                            </span>
                          )}
                          {product.stock_status === 'sold_out' && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="text-[9px] font-bold text-white bg-black/60 px-2 py-0.5 rounded">Sold Out</span>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Product info */}
                      <div className="p-2">
                        <p
                          className="text-[10px] font-semibold leading-tight truncate"
                          style={{ color: theme.profileTextColor || textColor }}
                        >
                          {product.product_name}
                        </p>
                        {product.price != null && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-[10px] font-bold" style={{ color: theme.profileTextColor || textColor }}>
                              {formatPrice(Number(product.price), product.currency)}
                            </span>
                            {product.compare_at_price != null && (
                              <span className="text-[8px] line-through opacity-50">
                                {formatPrice(Number(product.compare_at_price), product.currency)}
                              </span>
                            )}
                          </div>
                        )}
                        {/* CTA */}
                        <div
                          className="mt-1.5 text-center py-1 text-[9px] font-semibold rounded"
                          style={{
                            background: product.cta_style === 'filled' ? buttonColor : 'transparent',
                            color: product.cta_style === 'filled' ? buttonTextColor : buttonColor,
                            border: product.cta_style === 'outline' ? `1px solid ${buttonColor}` : 'none',
                            opacity: product.stock_status === 'sold_out' ? 0.5 : 1,
                          }}
                        >
                          {product.stock_status === 'sold_out' ? 'Sold Out' : product.cta_label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-4 text-[9px] opacity-30">
              Made with URLzippy
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
